import type { ReactNode } from 'react'
import { Dialog, WorkflowMark, cn, titleCase } from '@workflow/ui'

/**
 * The two-panel floating card the studio's "bring something in" moments live
 * in: a photograph on the left — the room the action happens in, kept in
 * monochrome so it belongs to every theme — and the caller's form on the
 * right. One shell, so opening a job, a client or a seat at the door all
 * carry the same shape: the generous rounding, the mark, the serif line,
 * and the small caps naming the register the card belongs to.
 *
 * The card holds one height through every step — a question with one field
 * and a question with five sit in the same frame, so moving forward never
 * makes the window jump. A step taller than the frame scrolls inside it.
 */
export function PosterDialog({
  open,
  onClose,
  labelledBy,
  image,
  alt,
  headline,
  caps,
  className,
  colorArtwork = false,
  children,
}: {
  open: boolean
  onClose: () => void
  labelledBy: string
  /** The photograph for the poster panel, served from the app's own ground. */
  image: string
  alt: string
  /** The serif line the poster says. */
  headline: string
  /** The small caps under the headline, naming the register. */
  caps: string
  /** Width overrides, when a card needs more room than the default. */
  className?: string
  /** Preserve the colors of artwork used by account and client setup. */
  colorArtwork?: boolean
  children: ReactNode
}) {
  return (
    <Dialog open={open} onClose={onClose} labelledBy={labelledBy} className={cn('sm:max-w-[44rem] lg:max-w-[54rem]', className)}>
      <div className="grid h-[min(38rem,calc(100dvh-2rem))] grid-rows-1 rounded-3xl border bg-surface shadow-modal sm:h-[min(38rem,calc(100dvh-4rem))] sm:grid-cols-[16rem_1fr] lg:grid-cols-[20rem_1fr]">
        <aside className="relative hidden flex-col justify-between overflow-hidden rounded-l-3xl p-7 text-white sm:flex lg:p-9">
          <img src={image} alt={alt} className={cn('absolute inset-0 size-full object-cover', !colorArtwork && 'grayscale')} />
          {/* The wash keeps the words readable and the photograph quiet —
              a room the card happens in, never the subject itself. */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/30" aria-hidden />
          <WorkflowMark size={20} className="relative" />
          <div className="relative">
            <p className="font-display text-[26px] leading-snug text-white lg:text-[30px]">{titleCase(headline)}</p>
            <p className="mt-3 text-micro uppercase text-white/60">{caps}</p>
          </div>
        </aside>
        <div className="flex min-h-0 flex-col overflow-y-auto [&>form]:flex-1">{children}</div>
      </div>
    </Dialog>
  )
}
